import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Logo from "../../components/assets/redLogo-removebg-preview.png";
import HeroImage from "../../components/assets/heroImage.png";
import { Card, Carousel, Dialog } from "@material-tailwind/react";
import Testimonials from "../../components/Testimonials";
import LanguageSelector from "../../components/LanguageSelector";
import {
  Briefcase,
  ClipboardCheck,
  Globe,
  GraduationCap,
  MessageCircle,
} from "lucide-react";
import { Link } from "react-router-dom";
import Contact from "../../components/forms/Contact";

const Landing = () => {
  const [t] = useTranslation();
  const [open, setOpen] = useState(false);

  const handleOpen = () => setOpen(!open);
  const navbarItems = t("navbar", { returnObjects: true });
  const whyus_reasons = t("reasons_why_us", { returnObjects: true });
  const testimonials = t("testimonials", { returnObjects: true });
  const highlights = t("services_highlights", { returnObjects: true });

  const heroRef = useRef(null);
  const whyUsRef = useRef(null);
  const testimonialsRef = useRef(null);
  const coursesRef = useRef(null);
  const contactRef = useRef(null);

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const scrollToSection = (id) => {
    const ref = refMapping[id];
    if (ref && ref.current) {
      ref.current.scrollIntoView({ behavior: "smooth" });
      setMobileMenuOpen(false);
    }
  };

  const iconMap = {
    1: (
      <div className="flex justify-center items-center mb-4 w-10 h-10 rounded-full bg-red-100 lg:h-12 lg:w-12">
        <svg
          className="w-5 h-5 text-red-600 lg:w-6 lg:h-6"
          fill="currentColor"
          viewBox="0 0 20 20"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fillRule="evenodd"
            d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z"
            clipRule="evenodd"
          ></path>
        </svg>
      </div>
    ),
    2: (
      <div className="flex justify-center items-center mb-4 w-10 h-10 rounded-full bg-red-100 lg:h-12 lg:w-12">
        <svg
          className="w-5 h-5 text-red-600 lg:w-6 lg:h-6"
          fill="currentColor"
          viewBox="0 0 20 20"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M7 3a1 1 0 000 2h6a1 1 0 100-2H7zM4 7a1 1 0 011-1h10a1 1 0 110 2H5a1 1 0 01-1-1zM2 11a2 2 0 012-2h12a2 2 0 012 2v4a2 2 0 01-2 2H4a2 2 0 01-2-2v-4z"></path>
        </svg>
      </div>
    ),
    3: (
      <div className="flex justify-center items-center mb-4 w-10 h-10 rounded-full bg-red-100 lg:h-12 lg:w-12">
        <svg
          className="w-5 h-5 text-red-600 lg:w-6 lg:h-6"
          fill="currentColor"
          viewBox="0 0 20 20"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3zM3.31 9.397L5 10.12v4.102a8.969 8.969 0 00-1.05-.174 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762zM9.3 16.573A9.026 9.026 0 007 14.935v-3.957l1.818.78a3 3 0 002.364 0l5.508-2.361a11.026 11.026 0 01.25 3.762 1 1 0 01-.89.89 8.968 8.968 0 00-5.35 2.524 1 1 0 01-1.4 0zM6 18a1 1 0 001-1v-2.065a8.935 8.935 0 00-2-.712V17a1 1 0 001 1z"></path>
        </svg>
      </div>
    ),
  };

  const coursesIconMap = {
    1: (
      <GraduationCap className="w-5 h-5 text-red-600 lg:w-6 lg:h-6 rounded-full" />
    ),
    2: <Briefcase className="w-5 h-5 text-red-600 lg:w-6 lg:h-6" />,
    3: <ClipboardCheck className="w-5 h-5 text-red-600 lg:w-6 lg:h-6" />,
    4: <MessageCircle className="w-5 h-5 text-red-600 lg:w-6 lg:h-6" />,
    5: <Globe className="w-5 h-5 text-red-600 lg:w-6 lg:h-6" />,
  };

  const refMapping = {
    1: heroRef,
    2: whyUsRef,
    3: testimonialsRef,
    4: coursesRef,
    5: contactRef,
  };

  return (
    <>
      <nav className="bg-white border-gray-200 px-4 lg:px-6 py-2.5 dark:bg-gray-800 fixed w-full z-50">
        <div className="flex flex-wrap justify-between items-center mx-auto max-w-screen-xl">
          <a href="#" className="flex items-center">
            <img src={Logo} className="mr-3 h-8 sm:h-12" alt="Konnect Logo" />
            <span className="self-center text-xl font-semibold whitespace-nowrap">
              Konnect Academy
            </span>
          </a>
          <div className="flex items-center lg:order-2">
            <LanguageSelector />
          </div>
          <div
            className={`${
              mobileMenuOpen ? "block" : "hidden"
            } justify-between items-center w-full lg:flex lg:w-auto lg:order-1`}
            id="mobile-menu-2"
          >
            <ul className="flex flex-col mt-4 font-medium lg:flex-row lg:space-x-8 lg:mt-0">
              {navbarItems.map((item, index) => (
                <li key={index}>
                  <a
                    onClick={() => scrollToSection(item.id)}
                    className=" cursor-pointer block py-2 pr-4 pl-3 text-gray-700 border-b border-gray-100 hover:bg-gray-50 lg:hover:bg-transparent lg:border-0 lg:hover:text-red-700 lg:p-0 dark:text-gray-400 lg:dark:hover:text-white dark:hover:bg-gray-700 dark:hover:text-white lg:dark:hover:bg-transparent dark:border-gray-700"
                  >
                    {item.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </nav>

      <section
        ref={heroRef}
        className="flex bg-white dark:bg-gray-900 min-h-[80vh] items-center justify-center py-12 px-4 sm:px-6 lg:px-8"
      >
        <div className="w-full max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row items-center">
            <div className="w-full lg:w-7/12 lg:pr-8">
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold leading-tight mb-4">
                {t(`hero.headline`)}
              </h1>
              <p className="text-base sm:text-lg md:text-xl lg:text-2xl font-light text-gray-500 dark:text-gray-400 mb-6">
                {t(`hero.subheadline`)}
              </p>
              <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3">
                <Link onClick={handleOpen} className="inline-flex items-center justify-center px-5 py-3 text-base font-medium text-center text-white rounded-lg bg-red-600 hover:bg-red-800 focus:ring-4 dark:focus:ring-blue-900">
                  {t(`hero.call_to_action`)}
                  <svg
                    className="w-5 h-5 ml-2 -mr-1"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    ></path>
                  </svg>
                </Link>

                <Link onClick={() => scrollToSection(5)} className="inline-flex items-center justify-center px-5 py-3 text-base font-medium text-center text-gray-900 border border-gray-300 rounded-lg hover:bg-gray-100 focus:ring-4 focus:ring-gray-100 dark:text-white dark:border-gray-700 dark:hover:bg-gray-700 dark:focus:ring-gray-800">
                  {t(`hero.contact_now`)}
                </Link>
              </div>
            </div>
            <div className="w-full lg:w-5/12 mt-8 lg:mt-0">
              <img
                className="rounded-full w-full h-auto max-w-md mx-auto"
                src={HeroImage}
                alt="mockup"
              />
            </div>
          </div>
        </div>
      </section>

      <section
        ref={whyUsRef}
        className="bg-gray-50 dark:bg-gray-800 py-12 px-4 sm:px-6 lg:px-8"
      >
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl mx-auto text-center mb-8 lg:mb-16">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 dark:text-white mb-4">
              {t(`why_us`)}
            </h2>
            <p className="text-base sm:text-xl text-gray-500 dark:text-gray-400">
              {t(`why_us_content`)}
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {whyus_reasons.map((reason) => (
              <Card
                key={reason.id}
                shadow={false}
                className="p-5 h-full shadow-xl shadow-gray-200 dark:shadow-gray-700"
              >
                <div className="flex flex-col h-full">
                  <div className="icon-container mb-4">
                    {iconMap[reason.id]}
                  </div>
                  <div className="reason-content flex-grow">
                    <h3 className="text-lg font-semibold mb-2">
                      {reason.title}
                    </h3>
                    <p>{reason.content}</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
          <div className="flex items-center justify-center animate-pulse mt-10">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-500 dark:text-gray-400">
              {t(`why_us_cta`)}
            </h2>
          </div>
        </div>
      </section>
      {/* Testimonials */}
      <section
        ref={testimonialsRef}
        className="bg-white dark:bg-gray-900 pb-20"
      >
        <div className="py-8 lg:py-16 mx-auto max-w-screen-xl px-4">
          <h2 className="mb-8 lg:mb-16 text-3xl font-extrabold tracking-tight leading-tight text-center text-gray-900 md:text-4xl">
            {t(`testimonials_header`)}
          </h2>
          <Carousel
            className="rounded-xl"
            autoplay={true}
            autoplayDelay={5000}
            loop={true}
            navigation={({ setActiveIndex, activeIndex, length }) => (
              <div className="absolute bottom-4 left-2/4 z-50 flex -translate-x-2/4 gap-2">
                {new Array(length).fill("").map((_, i) => (
                  <span
                    key={i}
                    className={`block h-1 cursor-pointer rounded-2xl transition-all content-[''] ${
                      activeIndex === i ? "w-8 bg-white" : "w-4 bg-white/50"
                    }`}
                    onClick={() => setActiveIndex(i)}
                  />
                ))}
              </div>
            )}
          >
            {testimonials.map((testimonial) => (
              <div
                key={testimonial.id}
                className="flex justify-center items-center h-full"
              >
                <Testimonials
                  name={testimonial.name}
                  profession={testimonial.profession}
                  testimonial={testimonial.testimonial}
                />
              </div>
            ))}
          </Carousel>
        </div>
      </section>

      <section
        ref={coursesRef}
        className="mb-10 md:mb-20 flex bg-white min-h-[70vh] items-center justify-center py-10 dark:bg-gray-800"
      >
        <div className="py-8 px-4 mx-auto max-w-screen-xl sm:py-16 lg:px-6">
          <div className="max-w-screen-md mb-8 lg:mb-16">
            <h2 className="mb-4 text-3xl md:text-4xl font-extrabold text-gray-900">
              {t(`courses_header`)}
            </h2>
            <p className="text-gray-500 sm:text-xl dark:text-gray-400">
              {t(`courses_content`)}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-10">
            {highlights.map((course) => (
              <Card
                key={course.id}
                shadow={false}
                className="p-5 h-auto md:h-96 shadow-2xl shadow-gray-200"
              >
                <div className="flex justify-center items-center mb-4 w-10 h-10 rounded-full bg-red-100 lg:h-12 lg:w-12">
                  {coursesIconMap[course.id]}
                </div>
                <div className="reason-content">
                  <h3 className="text-lg font-semibold my-4">{course.type}</h3>
                  <p>{course.description}</p>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section ref={contactRef} className="bg-white dark:bg-gray-900">
        <div className="py-8 px-4 mx-auto max-w-screen-xl sm:py-16 lg:px-6">
          <div className="mx-auto max-w-screen-sm text-center pt-10 md:pt-20">
            <h2 className="mb-4 text-3xl md:text-4xl font-extrabold leading-tight text-gray-900">
              {t(`get_envolved.subheader`)}
            </h2>
            <p className="mb-6 font-light text-gray-500 dark:text-gray-400 md:text-lg">
              {t(`get_envolved.content`)}
            </p>
            <Link
              onClick={handleOpen}
              className="text-white bg-red-600 hover:bg-red-800 focus:ring-4 font-medium rounded-lg text-sm px-5 py-2.5 mr-2 mb-2 dark:hover:bg-red-600 focus:outline-none"
            >
              {t(`get_envolved.btn`)}
            </Link>
          </div>
        </div>
      </section>

      <footer className="p-4 bg-gray-50 sm:p-6 dark:bg-gray-800">
        <div className="mx-auto max-w-screen-xl">
          <div className="md:flex md:justify-between">
            <div className="mb-6 md:mb-0">
              <Link className="flex items-center">
                <img
                  src={Logo}
                  className="mr-3 h-16 md:h-20"
                  alt="konecta Logo"
                />
                <span className="self-center text-xl md:text-2xl font-semibold whitespace-nowrap">
                  Konecta Academy
                </span>
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 sm:gap-6">
              <div>
                <h2 className="mb-6 text-sm font-semibold text-gray-900 uppercase">
                  {t(`find_us`)}
                </h2>
                <ul className="text-gray-600 dark:text-gray-400">
                  <li className="mb-4">
                    <Link
                      to="https://www.linkedin.com/company/konnect-academy/"
                      className="hover:underline cursor-pointer"
                    >
                      Linked In
                    </Link>
                  </li>
                </ul>
              </div>
              <div>
                <h2 className="mb-6 text-sm font-semibold text-gray-900 uppercase">
                  {t(`contact_us`)}
                </h2>
                <ul className="text-gray-600 dark:text-gray-400">
                  <li className="mb-4">
                    <p className="hover:underline cursor-pointer">
                      +244 958421189
                    </p>
                  </li>
                  <li>
                    <p className="hover:underline cursor-pointer">
                      +244 945213227
                    </p>
                  </li>
                </ul>
              </div>
            </div>
          </div>
          <hr className="my-6 border-gray-200 sm:mx-auto dark:border-gray-700 lg:my-8" />
          <div className="text-center">
            <span className="text-sm text-gray-500 dark:text-gray-400">
              © 2024{" "}
              <a href="#" className="hover:underline">
                Konecta academy
              </a>
              . All Rights Reserved.
            </span>
          </div>
        </div>
      </footer>

      <Dialog open={open} handler={handleOpen}>
        <Contact handleOpen={handleOpen} />
      </Dialog>
    </>
  );
};

export default Landing;
